import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { HospitalDto } from "@ambulance/shared";
import { useApi } from "../api";
import { Loader2, Search, AlertCircle } from "lucide-react";

type BagRow = { _id: string; hospitalId: string; hospitalName: string; date: string; shift: string; checkedByName: string; checkedByBadge: string; submittedAt: number };

export function BagChecklistsPage() {
  const api = useApi();
  const hospitalsQ = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const hospitals = hospitalsQ.data?.map((h) => ({ ...h, _id: h.id }));
  const [hospitalFilter, setHospitalFilter] = useState<string>("");
  const [search, setSearch] = useState("");

  const listQ = useQuery({
    queryKey: ["bag-checklists", hospitalFilter, api.token],
    queryFn: () => api.get<BagRow[]>(`/api/v1/bag-checklists?limit=200${hospitalFilter ? `&hospitalId=${hospitalFilter}` : ""}`),
    enabled: Boolean(api.token),
  });
  const checklists = listQ.data;

  const filtered = useMemo(() => {
    if (!checklists) return [];
    if (!search) return checklists;
    const q = search.toLowerCase();
    return checklists.filter(
      (c) =>
        c.hospitalName.toLowerCase().includes(q) ||
        c.checkedByName?.toLowerCase().includes(q)
    );
  }, [checklists, search]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Bag Checklists</h1>
        <Link
          to="/bag-checklist"
          className="rounded-lg bg-[var(--hmg-red)] hover:bg-[var(--hmg-red-dark)] text-white text-sm font-semibold px-4 py-2"
        >
          + New Bag Checklist
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search by hospital or checker…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
          />
        </div>
        <select
          value={hospitalFilter}
          onChange={(e) => setHospitalFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--hmg-red)]"
        >
          <option value="">All my hospitals</option>
          {hospitals?.map((h) => (
            <option key={h._id} value={h._id}>
              {h.name}
            </option>
          ))}
        </select>
      </div>

      {checklists === undefined ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--hmg-red)]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">
            {checklists.length === 0
              ? "No bag checklists submitted yet."
              : "No results match your filters."}
          </p>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Hospital</th>
                    <th className="px-4 py-3">Shift</th>
                    <th className="px-4 py-3">Checked by</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((c) => (
                    <tr key={c._id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <div className="font-medium text-slate-900">{c.date}</div>
                        <div className="text-xs text-slate-500">
                          {new Date(c.submittedAt).toLocaleString(undefined, { hour12: false })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {c.hospitalName}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        <span
                          className={`inline-block text-xs px-2 py-0.5 rounded ${c.shift === "D" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}
                        >
                          {c.shift === "D" ? "Day" : "Night"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">
                        {c.checkedByName || "—"}
                        {c.checkedByBadge ? ` (${c.checkedByBadge})` : ""}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Link
                          to="/bag-checklists/$id"
                          params={{ id: c._id }}
                          className="text-sm font-semibold text-[var(--hmg-red)]"
                        >
                          View &rarr;
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="md:hidden space-y-3">
            {filtered.map((c) => (
              <Link
                key={c._id}
                to="/bag-checklists/$id"
                params={{ id: c._id }}
                className="block bg-white rounded-xl border border-slate-200 p-4 hover:bg-slate-50"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold text-slate-900">{c.date}</div>
                  <span
                    className={`inline-block text-xs px-2 py-0.5 rounded ${c.shift === "D" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}
                  >
                    {c.shift === "D" ? "Day" : "Night"}
                  </span>
                </div>
                <div className="text-sm text-slate-700 mb-1">{c.hospitalName}</div>
                <div className="text-xs text-slate-600">
                  Checked by {c.checkedByName || "—"}
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
